import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Download, Loader2, X, MessageCircle, History, Clock, RefreshCw } from 'lucide-react';
import LessonNoteChatbot from '../components/admin/LessonNoteChatbot';

const COURT_DOCUMENTS = [
  "Amended Pleadings",
  "Committal Order",
  "Counter Claim against Other Party",
  "Decree Absolute",
  "Decree Nisi",
  "Extension of Time",
  "Final Judgment",
  "Garnishee Order",
  "Interlocutory Injunction",
  "Interlocutory Judgment",
  "List of Disputed Points",
  "List of Evidence/Exhibits",
  "Motion Ex Parte",
  "Motion on Notice",
  "Reply to Defense",
  "Settlement Order",
  "Statement of Claim",
  "Statement of Defense",
  "Stay of Execution",
  "Summary of Claim",
  "Witness Statement",
  "Writ of Delivery",
  "Writ of Execution",
  "Writ of Possession",
  "Writ of Summons"
];

const NON_COURT_DOCUMENTS = [
  "Affidavit in Opposition",
  "Affidavit in Support",
  "Affidavit of Change of Date of Birth",
  "Affidavit of Change of Name",
  "Affidavit of Identity",
  "Affidavit of Means",
  "Affidavit of Service",
  "Employment Agreement",
  "Rent Agreement",
  "Lease Agreement",
  "Sale & Purchase Agreement",
  "Loan Agreement",
  "Service Agreement",
  "Partnership Agreement",
  "Sublease Agreement",
  "Mortgage Deed",
  "Memorandum of Understanding",
  "Insurance Contract",
  "Child Adoption Agreement",
  "Child Custody Agreement",
  "Will & Testament",
  "Deed of Trust",
  "Statutory Declaration (e.g., Correction of document details, nationality, marital status, etc.)",
  "Power of Attorney"
];

const COURT_CASE_TYPES = [
  "Land & Property Cases",
  "Contract Cases",
  "Family & Matrimonial Cases",
  "Commercial & Corporate Cases",
  "Employment & Labour Cases",
  "Tort (e.g., Defamation) Cases",
  "Estate & Succession Cases",
  "Equity & Trusts Cases",
  "Financial & Debt Recovery Cases"
];

const COURT_LEVELS = [
  "High Court",
  "Circuit Court",
  "District Court",
  "Magistrate Court"
];

const DocumentGenerator = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    documentType: '', // 'Court Document' or 'Non-Court Document'
    courtDocumentName: '',
    nonCourtDocumentName: '',
    courtCaseType: '',
    courtLevel: '',
    courtLocation: '',
    description: ''
  });

  const [chatSession, setChatSession] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [showChatbot, setShowChatbot] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');
  const [pricing, setPricing] = useState({ price: 1.0, formatted: "GHS 1.00" });
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [chatbotComplete, setChatbotComplete] = useState(false);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  // Recent Documents state
  const [showRecentDocs, setShowRecentDocs] = useState(false);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const [userEmail, setUserEmail] = useState(localStorage.getItem('legal_doc_email') || '');

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPricing();
    handlePaymentCallback();
  }, []);

  // Backup effect: If chatbotComplete is true but preview hasn't started after 2 seconds, trigger it
  useEffect(() => {
    if (chatbotComplete && !showPreview && !isGeneratingPreview && chatSession) {
      console.log('🔄 Backup: Triggering preview generation from useEffect');
      const timer = setTimeout(() => {
        if (!showPreview && !isGeneratingPreview) {
          console.log('🔄 Backup: Actually calling generatePreview');
          generatePreview(chatSession);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [chatbotComplete, showPreview, isGeneratingPreview, chatSession]);

  const handlePaymentCallback = async () => {
    // Check if we're returning from payment
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const reference = urlParams.get('reference');
    let session_id = urlParams.get('session_id');
    
    // Fallback to localStorage if session_id is missing or 'None' from URL
    if (!session_id || session_id === 'None' || session_id === 'null') {
      session_id = localStorage.getItem('legal_doc_session_id');
      console.log('📋 Retrieved session_id from localStorage:', session_id);
    }

    if (paymentStatus === 'success' && reference && session_id) {
      console.log('✅ Payment successful, generating document...');
      console.log('   Reference:', reference);
      console.log('   Session ID:', session_id);
      setShowChatbot(true);
      setChatSession(session_id);
      setIsGeneratingDocument(true);

      try {
        const response = await fetch(`${API_BASE}/api/legal-document/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: session_id,
            payment_reference: reference
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Generate document error response:', errorText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Document generated:', data);
        setGeneratedDocument(data);
        setPaymentSuccess(true);
        
        // Clean up localStorage and URL
        localStorage.removeItem('legal_doc_session_id');
        window.history.replaceState({}, document.title, '/document-generator');
      } catch (error) {
        console.error('Document generation error:', error);
        alert(`Failed to generate document: ${error.message}`);
      } finally {
        setIsGeneratingDocument(false);
      }
    } else if (paymentStatus === 'success' && reference && !session_id) {
      console.error('Payment successful but no session_id available');
      console.error('This usually happens when testing in preview but payment callback goes to production.');
      console.error('Please deploy to production and test there, or contact support.');
      alert('Payment was successful but session information was lost.\n\nThis can happen when testing across different environments (preview vs production).\n\nPlease contact support with reference: ' + reference + '\n\nOr deploy to production and test there.');
      window.history.replaceState({}, document.title, '/document-generator');
    } else if (paymentStatus === 'failed') {
      alert('Payment was not successful. Please try again.');
      localStorage.removeItem('legal_doc_session_id');
      window.history.replaceState({}, document.title, '/document-generator');
    } else if (paymentStatus === 'error') {
      alert('Payment verification error. Please contact support.');
      localStorage.removeItem('legal_doc_session_id');
      window.history.replaceState({}, document.title, '/document-generator');
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/legal-document/pricing`);
      if (response.ok) {
        const data = await response.json();
        setPricing(data);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isFormValid = () => {
    if (!formData.documentType) return false;
    if (!formData.description) return false;

    if (formData.documentType === 'Court Document') {
      return formData.courtDocumentName && 
             formData.courtCaseType && 
             formData.courtLevel && 
             formData.courtLocation;
    } else {
      return formData.nonCourtDocumentName;
    }
  };

  const handlePayment = async () => {
    if (!chatSession || !chatbotComplete) {
      alert('Please complete the chatbot conversation first');
      return;
    }

    setIsProcessingPayment(true);
    try {
      // Build form_data for payment
      const form_data = {
        documentType: formData.documentType === 'Court Document' 
          ? formData.courtDocumentName 
          : formData.nonCourtDocumentName,
        description: formData.description,
        documentCategory: formData.documentType === 'Court Document' ? 'court_case' : 'non_court'
      };

      const response = await fetch(`${API_BASE}/api/legal-document/create-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: 'user@example.com', // TODO: Get from authenticated user
          form_data: form_data,
          session_id: chatSession
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Payment response:', data);
      
      if (data.payment_url || data.authorization_url) {
        // Store session_id in localStorage so we can retrieve it after payment redirect
        localStorage.setItem('legal_doc_session_id', chatSession);
        window.location.href = data.payment_url || data.authorization_url;
      } else {
        console.error('Payment response:', data);
        throw new Error('No payment URL received from server');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert(`Payment initialization failed: ${error.message}`);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const startChatbot = async () => {
    if (!isFormValid()) {
      alert('Please fill in all required fields before starting the chatbot');
      return;
    }

    setShowChatbot(true);
    setIsChatLoading(true);

    // Build context for AI
    const documentName = formData.documentType === 'Court Document' 
      ? formData.courtDocumentName 
      : formData.nonCourtDocumentName;

    let context = `Document Type: ${formData.documentType}\nDocument Name: ${documentName}\nDescription: ${formData.description}`;

    if (formData.documentType === 'Court Document') {
      context += `\nCourt Case Type: ${formData.courtCaseType}\nCourt Level: ${formData.courtLevel}\nCourt Location: ${formData.courtLocation}`;
      context += `\n\nIMPORTANT AI RULES:\n1. Use "${documentName}" as the base for the document title, but refine it based on user details to match Ghana legal standards.\n2. The document must have a court heading in this format:\nIN THE ${formData.courtLevel.toUpperCase()} OF JUSTICE\n${formData.courtLocation.toUpperCase()}\n3. Contextualize content using the ${formData.courtCaseType} case type.`;
    } else {
      context += `\n\nIMPORTANT AI RULES:\n1. Use "${documentName}" as the document title, refined based on user details to match Ghana legal standards.`;
    }

    try {
      // Build form_data object for backend (use camelCase keys to match backend expectations)
      const form_data = {
        documentType: formData.documentType === 'Court Document' 
          ? formData.courtDocumentName 
          : formData.nonCourtDocumentName,
        description: formData.description,
        documentCategory: formData.documentType === 'Court Document' ? 'court_case' : 'non_court'
      };

      if (formData.documentType === 'Court Document') {
        form_data.courtType = formData.courtLevel;
        form_data.courtLocation = formData.courtLocation;
        form_data.caseType = formData.courtCaseType;
      }

      const response = await fetch(`${API_BASE}/api/legal-document/chatbot/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_data })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Chatbot started:', data);
      setChatSession(data.session_id);
      setChatMessages([{ role: 'assistant', content: data.message || data.initial_message }]);
      
      // Check if conversation is already complete from the start (all fields filled)
      if (data.status === 'completed' || (data.missing_fields && data.missing_fields.length === 0)) {
        console.log('✅ Chatbot conversation completed on start - ready for payment');
        setChatbotComplete(true);
      }
    } catch (error) {
      console.error('Chatbot init error:', error);
      alert(`Failed to start chatbot: ${error.message}`);
      setShowChatbot(false);
    } finally {
      setIsChatLoading(false);
    }
  };

  const sendChatMessage = async () => {
    if (!currentMessage.trim() || !chatSession) return;

    const userMessage = currentMessage;
    setCurrentMessage('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsChatLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/legal-document/chatbot/${chatSession}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Chat response:', data);
      console.log('Chat response status:', data.status);
      setChatMessages(prev => [...prev, { role: 'assistant', content: data.message || data.response }]);
      
      // Check if conversation is complete - ONLY rely on status field
      // Note: missing_fields is always [] from backend, so we check status only
      if (data.status === 'completed') {
        console.log('✅ Chatbot conversation completed - generating preview');
        setChatbotComplete(true);
        // Use session_id from response which is guaranteed to be correct
        const sessionIdToUse = data.session_id || chatSession;
        console.log('Session ID for preview:', sessionIdToUse);
        // Generate preview with await to ensure it runs
        await generatePreview(sessionIdToUse);
      } else {
        console.log('Chatbot still in progress, status:', data.status);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, { role: 'assistant', content: `Sorry, an error occurred: ${error.message}` }]);
    } finally {
      setIsChatLoading(false);
    }
  };

  // Generate document preview (before payment)
  const generatePreview = async (sessionId = null) => {
    // Use passed sessionId or fall back to state
    const activeSession = sessionId || chatSession;
    
    console.log('🔄 generatePreview called with sessionId:', sessionId);
    console.log('🔄 chatSession state value:', chatSession);
    console.log('🔄 activeSession to use:', activeSession);
    
    if (!activeSession) {
      console.error('No chat session available for preview');
      // Still show preview section with fallback
      setDocumentPreview({ 
        preview_image: null, 
        preview_text: 'Preview could not be generated. You can still proceed to payment.',
        document_type: formData.documentType === 'Court Document' ? formData.courtDocumentName : formData.nonCourtDocumentName
      });
      setShowPreview(true);
      return;
    }
    
    console.log('🔄 Setting isGeneratingPreview to true');
    setIsGeneratingPreview(true);
    try {
      console.log('🔄 Generating preview for session:', activeSession);
      const response = await fetch(`${API_BASE}/api/legal-document/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: activeSession
        })
      });
      
      console.log('🔄 Preview response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Preview API error:', response.status, errorText);
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Preview generated successfully');
      setDocumentPreview(data);
      setShowPreview(true);
    } catch (error) {
      console.error('Preview generation error:', error);
      // Still show preview section with a fallback message
      setDocumentPreview({ 
        preview_image: null, 
        preview_text: 'Preview could not be generated. You can still proceed to payment to generate your document.',
        document_type: formData.documentType === 'Court Document' ? formData.courtDocumentName : formData.nonCourtDocumentName
      });
      setShowPreview(true);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const generateDocument = async () => {
    if (!chatSession) {
      alert('Please complete the chatbot conversation first');
      return;
    }

    setIsGeneratingDocument(true);
    try {
      // Note: This would normally require payment_reference from Paystack
      // For now, we'll use a placeholder
      const response = await fetch(`${API_BASE}/api/legal-document/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: chatSession,
          payment_reference: 'test_payment_ref_' + Date.now() // TODO: Get from actual Paystack payment
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Document generated:', data);
      setGeneratedDocument(data);
      setPaymentSuccess(true);
    } catch (error) {
      console.error('Document generation error:', error);
      alert(`Failed to generate document: ${error.message}`);
    } finally {
      setIsGeneratingDocument(false);
    }
  };

  const downloadDocument = () => {
    if (generatedDocument?.docx_file) {
      // The docx_file is base64 encoded, download it directly
      const linkSource = `data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,${generatedDocument.docx_file}`;
      const downloadLink = document.createElement('a');
      const fileName = `${generatedDocument.title || 'Legal_Document'}.docx`;
      
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
    } else if (generatedDocument?.document_url) {
      window.open(`${API_BASE}${generatedDocument.document_url}`, '_blank');
    } else {
      alert('No document available to download');
    }
  };

  // Fetch recent documents
  const fetchRecentDocuments = async () => {
    if (!userEmail) {
      alert('Please enter your email to view your documents');
      return;
    }
    
    // Save email for future use
    localStorage.setItem('legal_doc_email', userEmail);
    
    setIsLoadingRecent(true);
    try {
      const response = await fetch(`${API_BASE}/api/legal-document/recent?email=${encodeURIComponent(userEmail)}&limit=10`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setRecentDocuments(data.documents || []);
      setShowRecentDocs(true);
    } catch (error) {
      console.error('Error fetching recent documents:', error);
      alert('Failed to fetch your documents. Please try again.');
    } finally {
      setIsLoadingRecent(false);
    }
  };

  // Download a recent document
  const downloadRecentDocument = async (documentId, title) => {
    try {
      const response = await fetch(`${API_BASE}/api/legal-document/download/${documentId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.docx_file) {
        const linkSource = `data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,${data.docx_file}`;
        const downloadLink = document.createElement('a');
        const fileName = `${title || data.title || 'Legal_Document'}.docx`;
        
        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();
      } else {
        alert('Document file not available');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document. Please try again.');
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown date';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Recent Documents Toggle */}
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-2 text-amber-800">
                <History className="h-5 w-5" />
                <span className="font-medium">Have you generated documents before?</span>
              </div>
              <div className="flex flex-1 gap-2 w-full sm:w-auto">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  className="flex-1 bg-white"
                  data-testid="recent-docs-email"
                />
                <Button 
                  onClick={fetchRecentDocuments}
                  disabled={isLoadingRecent || !userEmail}
                  variant="outline"
                  className="border-amber-300 hover:bg-amber-100"
                  data-testid="view-recent-docs-btn"
                >
                  {isLoadingRecent ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      View My Documents
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Documents List */}
        {showRecentDocs && (
          <Card data-testid="recent-documents-section">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-600" />
                  Your Recent Documents
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowRecentDocs(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {recentDocuments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No documents found for this email.</p>
                  <p className="text-sm mt-1">Generate your first document below!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentDocuments.map((doc) => (
                    <div 
                      key={doc.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border hover:border-blue-300 transition-colors"
                      data-testid={`recent-doc-${doc.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">
                          {doc.document_type || doc.title || 'Legal Document'}
                        </h4>
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(doc.created_at)}
                        </p>
                      </div>
                      <Button
                        onClick={() => downloadRecentDocument(doc.id, doc.document_type || doc.title)}
                        size="sm"
                        className="ml-4 bg-blue-600 hover:bg-blue-700"
                        data-testid={`download-doc-${doc.id}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Main Document Generator Card */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <FileText className="h-6 w-6" />
                  Legal Document Generator
                </CardTitle>
                <CardDescription>
                  Generate professional legal documents tailored to Ghana&apos;s legal standards
                </CardDescription>
                
                {/* Instruction Message with WhatsApp Support */}
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">📝 How it works:</span> Fill the form and make payment to generate your document. If you face any difficulty, click the WhatsApp link below to chat with us for assistance!
                  </p>
                  <a 
                    href="https://wa.me/233547403851?text=Hello%20EduLegalHub,%20I%20need%20assistance%20with%20generating%20a%20legal%20document."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Chat with us on WhatsApp
                  </a>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Document Generation Loading */}
            {isGeneratingDocument && !generatedDocument && (
              <div className="text-center py-12">
                <Loader2 className="h-12 w-12 animate-spin mx-auto text-blue-600 mb-4" />
                <h3 className="text-xl font-semibold mb-2">Generating Your Document</h3>
                <p className="text-gray-600">Please wait while we create your professional legal document...</p>
              </div>
            )}

            {!showChatbot && !paymentSuccess && !isGeneratingDocument && (
              <>
                {/* Document Type */}
                <div className="space-y-2">
                  <Label>Document Type *</Label>
                  <Select value={formData.documentType} onValueChange={(val) => handleFieldChange('documentType', val)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Court Document">Court Document</SelectItem>
                      <SelectItem value="Non-Court Document">Non-Court Document</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Court Document Fields */}
                {formData.documentType === 'Court Document' && (
                  <>
                    <div className="space-y-2">
                      <Label>Court Document Name *</Label>
                      <Select value={formData.courtDocumentName} onValueChange={(val) => handleFieldChange('courtDocumentName', val)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select court document" />
                        </SelectTrigger>
                        <SelectContent>
                          {COURT_DOCUMENTS.map(doc => (
                            <SelectItem key={doc} value={doc}>{doc}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Court Case Type *</Label>
                      <Select value={formData.courtCaseType} onValueChange={(val) => handleFieldChange('courtCaseType', val)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select case type" />
                        </SelectTrigger>
                        <SelectContent>
                          {COURT_CASE_TYPES.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Court Level *</Label>
                      <Select value={formData.courtLevel} onValueChange={(val) => handleFieldChange('courtLevel', val)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select court level" />
                        </SelectTrigger>
                        <SelectContent>
                          {COURT_LEVELS.map(level => (
                            <SelectItem key={level} value={level}>{level}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Court Location (Town/City) *</Label>
                      <Input
                        value={formData.courtLocation}
                        onChange={(e) => handleFieldChange('courtLocation', e.target.value)}
                        placeholder="e.g., Accra, Kumasi"
                      />
                    </div>
                  </>
                )}

                {/* Non-Court Document Fields */}
                {formData.documentType === 'Non-Court Document' && (
                  <div className="space-y-2">
                    <Label>Non-Court Document Name *</Label>
                    <Select value={formData.nonCourtDocumentName} onValueChange={(val) => handleFieldChange('nonCourtDocumentName', val)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select document" />
                      </SelectTrigger>
                      <SelectContent>
                        {NON_COURT_DOCUMENTS.map(doc => (
                          <SelectItem key={doc} value={doc}>{doc}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Description */}
                {formData.documentType && (
                  <div className="space-y-2">
                    <Label>Document Description/Details *</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => handleFieldChange('description', e.target.value)}
                      placeholder="Provide details about your document. The AI will use this to ask relevant questions and generate your document."
                      rows={5}
                    />
                  </div>
                )}

                {/* Actions */}
                {formData.documentType && (
                  <div className="flex flex-col gap-4 pt-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-700">
                        Document Cost: <span className="font-bold text-lg">{pricing.formatted}</span>
                      </p>
                      <p className="text-xs text-gray-600 mt-2">
                        AI Assistant will help gather all necessary details before payment
                      </p>
                    </div>

                    <Button 
                      onClick={startChatbot} 
                      disabled={!isFormValid()}
                      className="w-full"
                    >
                      Continue to AI Assistant
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Chatbot */}
            {showChatbot && !paymentSuccess && !showPreview && (
              <div className="space-y-4">
                <div className="bg-white border rounded-lg p-4 max-h-96 overflow-y-auto" data-testid="chatbot-messages">
                  {chatMessages.map((msg, idx) => (
                    <div key={idx} className={`mb-4 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                      <div className={`inline-block p-3 rounded-lg ${msg.role === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-100'}`}>
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {isChatLoading && <div className="text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>}
                </div>

                {!chatbotComplete && (
                  <div className="flex gap-2">
                    <Input
                      value={currentMessage}
                      onChange={(e) => setCurrentMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                      placeholder="Type your response..."
                      data-testid="chat-input"
                    />
                    <Button onClick={sendChatMessage} disabled={isChatLoading} data-testid="send-message-btn">
                      Send
                    </Button>
                  </div>
                )}

                {chatbotComplete && !showPreview && (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                      <p className="text-green-800 font-medium">✓ All details collected successfully!</p>
                      <p className="text-sm text-green-700 mt-1">Generating document preview...</p>
                    </div>
                    
                    {isGeneratingPreview && (
                      <div className="text-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-2" />
                        <p className="text-gray-600">Generating preview...</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Document Preview (Image-based - non-copyable) */}
            {showPreview && !paymentSuccess && (
              <div className="space-y-4" data-testid="document-preview-section">
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">📄 Document Preview</h3>
                  <p className="text-sm text-blue-700">
                    Review your document preview below. Complete payment to download the full editable Word document.
                  </p>
                </div>

                {/* Secure Image Preview - prevents copying */}
                <div 
                  className="relative border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50"
                  style={{
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none'
                  }}
                  onContextMenu={(e) => e.preventDefault()}
                  data-testid="preview-image-container"
                >
                  {documentPreview?.preview_image ? (
                    <img 
                      src={`data:image/png;base64,${documentPreview.preview_image}`}
                      alt="Document Preview"
                      className="w-full h-auto"
                      style={{
                        pointerEvents: 'none',
                        userSelect: 'none',
                        WebkitUserDrag: 'none'
                      }}
                      draggable="false"
                      data-testid="preview-image"
                    />
                  ) : (
                    <div className="p-8 text-center">
                      <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-600">Preview not available. Your document is ready for generation.</p>
                    </div>
                  )}
                  
                  {/* Overlay to further prevent interaction */}
                  <div 
                    className="absolute inset-0 bg-transparent"
                    style={{ pointerEvents: 'none' }}
                  />
                </div>

                {/* Payment Section */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 p-6 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-semibold text-gray-800">Ready to Download</h4>
                      <p className="text-sm text-gray-600">Get your full editable Word document</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Document Cost</p>
                      <p className="text-2xl font-bold text-green-600">{pricing.formatted}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setShowPreview(false);
                        setChatbotComplete(false);
                        setDocumentPreview(null);
                      }}
                      className="flex-1"
                      data-testid="back-to-chat-btn"
                    >
                      ← Back to Edit
                    </Button>
                    <Button 
                      onClick={handlePayment} 
                      disabled={isProcessingPayment}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      data-testid="proceed-to-payment-btn"
                    >
                      {isProcessingPayment ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Pay & Download
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Success */}
            {paymentSuccess && generatedDocument && (
              <div className="text-center space-y-4">
                <div className="text-green-600 text-xl font-bold">✓ Document Generated Successfully!</div>
                <Button onClick={downloadDocument} className="w-full">
                  <Download className="h-4 w-4 mr-2" />
                  Download Document
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DocumentGenerator;
